"use client";

import { useCallback, useRef, useState } from "react";

export const ADMIN_DIALOG_CLOSE_DELAY_MS = 150;

export const ADMIN_DIALOG_WIDTH_CLASS = {

  sm: "max-w-[min(360px,calc(100%-30px))]",

  md: "max-w-[min(640px,calc(100%-30px))]",

  xl: "max-w-[min(840px,calc(100%-30px))]",

  lg: "max-w-[min(1200px,calc(100%-30px))]",
} as const;

export const adminDialogContentClass =
  "gap-0 overflow-hidden p-0 data-open:zoom-in-100 data-closed:zoom-out-100 w-full";

export const adminDialogBodyClass = "max-h-[520px] overflow-y-auto pr-1";

export function useAdminDialog<T>(delayMs = ADMIN_DIALOG_CLOSE_DELAY_MS) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<T | null>(null);
  const closeTimerRef = useRef<number | null>(null);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const openDialog = useCallback(
    (next: T) => {
      clearCloseTimer();
      setData(next);
      setOpen(true);
    },
    [clearCloseTimer],
  );

  const closeDialog = useCallback(() => {
    setOpen(false);
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => {
      setData(null);
      closeTimerRef.current = null;
    }, delayMs);
  }, [clearCloseTimer, delayMs]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean, blocked = false) => {
      if (!nextOpen && !blocked) {
        closeDialog();
      }
    },
    [closeDialog],
  );

  const updateData = useCallback((updater: (current: T) => T) => {
    setData((current) => (current ? updater(current) : current));
  }, []);

  return {
    open,
    data,
    openDialog,
    closeDialog,
    handleOpenChange,
    updateData,
  };
}
